
import _, { templateSettings } from "lodash";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";

const globals = namespace("globals");

@Component({
    components: {}
})
export default class CompHeroHeadline extends Vue {
  @Prop() src!: string;
  @Prop() title!: string;
  @Prop() width!: string;
  @Prop() height!: string;
  @Prop() compSettings!: string;
  @Prop() fillContainer?: boolean;
  @Prop() imagePosition!: string; // neu: z.B. "left top", "right center", "top", "bottom right"
  @Prop() altText!: string;

  settings: any = null;
  textAlign: string = 'center';
  transparentGradient: boolean = false;
  darkMode: string = 'auto'; // "auto", "lighten", "darken"

  // neue Felder für natürliche Bildgröße
  imgNaturalWidth = 0;
  imgNaturalHeight = 0;
  imgLoaded = false;

  $refs!: {
    zoomer: any;
    heroTitle?: HTMLElement;
  };
  computedFontSize: string | null = null;

  mounted() {
    if (this.compSettings) {
      // console.log("CompHeroHeadline mounted with compSettings:", this.compSettings);
      this.settings = JSON.parse(this.compSettings);
      this.textAlign = this.settings?.textAlign ?? 'center';
      this.transparentGradient = this.settings?.transparentGradient ?? false;
      this.darkMode = this.settings?.darkMode ?? 'auto';
    }
    // adjust font after mount
    this.$nextTick(() => this.adjustTitleFontSize());
    window.addEventListener('resize', this.onWindowResize);
  }

  @Watch("compSettings")
  async onCompSettingsChange() {
    if (this.compSettings) {
      this.settings = JSON.parse(this.compSettings);
      this.textAlign = this.settings?.textAlign ?? 'center';
      this.transparentGradient = this.settings?.transparentGradient ?? false;
      this.darkMode = this.settings?.darkMode ?? 'auto';
    }
    this.$nextTick(() => this.adjustTitleFontSize());
  }

  @Watch('title')
  onTitleChanged() {
    this.$nextTick(() => this.adjustTitleFontSize());
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = () => {
    this.adjustTitleFontSize();
  }

  get headlineTitel() {
    if (this.title) {
      return this.title;
    }
    return this.settings?.title ?? '';
  }

  get isFillContainer() {
    if (this.fillContainer !== undefined && this.fillContainer !== null)
      return this.fillContainer;
    return this.settings?.fillContainer ?? false;
  }

  get imagePos() {
    if (this.imagePosition && this.imagePosition.trim().length > 0) {
      return this.imagePosition.trim();
    }
    if (!this.settings?.imagePosition || this.settings?.imagePosition.trim().length === 0) {
      return "center";
    }
    return this.settings?.imagePosition;
  }

  get overlayStyle() {
    const isDark = ((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark) ? true : false;
    const forcedDark = this.darkMode === 'darken' ? true : (this.darkMode === 'lighten' ? false : null);
    const effectiveDark = forcedDark === null ? isDark : forcedDark;
    const rgb = effectiveDark ? '35,35,35' : '255,255,255';
    const align = (this.textAlign || 'center').toString().trim().toLowerCase();

    // If transparentGradient is disabled, use a uniform overlay to dim/brighten the whole image
    if (!this.transparentGradient) {
      const alpha = effectiveDark ? 0.55 : 0.60; // stronger darkening in dark mode, lighter brightening in light mode
      return { background: `rgba(${rgb},${alpha})` };
    }

    const solid = `rgba(${rgb}, 0.85)`; // fully opaque at start for gradient
    const transparent = `rgba(${rgb}, 0)`;
    // slower fade: keep solid longer and add intermediate semi-transparent stop
    if (align === 'left') {
      return { background: `linear-gradient(to right, ${solid} 0%, rgba(${rgb}, 0.6) 60%, ${transparent} 100%)` };
    }
    if (align === 'right') {
      return { background: `linear-gradient(to left, ${solid} 0%, rgba(${rgb}, 0.6) 60%, ${transparent} 100%)` };
    }
    return { background: `radial-gradient(circle at center, ${solid} 0%, rgba(${rgb}, 0.65) 35%, ${transparent} 100%)` };
  }

  get titleStyle() {
    const isDark = ((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark) ? true : false;
    const forcedDark = this.darkMode === 'darken' ? true : (this.darkMode === 'lighten' ? false : null);
    const effectiveDark = forcedDark === null ? isDark : forcedDark;
    let color = '';
    // let accent = this.$store.state.ux.colorAccent;
    // if (accent) {
    //   try {
    //     color = this.adjustHexBrightness(accent, effectiveDark ? 18 : -10);
    //   } catch (e) {
    //     color = accent; // fallback to original if parsing failed
    //   }
    // }
    if (!color) color = effectiveDark ? '#e0e0e0' : '#5a5a5a';

    // indent for left/right aligned titles will be adjusted based on container width
    let indent = '30px';
    // find container Width (v-img wrapper)
    const el = (this.$refs.heroTitle as unknown) as HTMLElement;
    if (el) {
      const container = this.$el.querySelector('.img-pos') as HTMLElement || el.parentElement;
      const containerWidth = container ? container.clientWidth : el.clientWidth;
      if (containerWidth && containerWidth > 0) {
        if (containerWidth < 500) {
          indent = '15px';
        }
      }
    }
    const align = (this.textAlign || 'center').toString().trim().toLowerCase();

    const base: any = {
      color,
      zIndex: 2,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
      fontWeight: 700,
      // use computedFontSize when available to ensure wrapped text fits
      fontSize: this.computedFontSize ? this.computedFontSize : 'clamp(1.25rem, 4vw, 2.2rem)',
      lineHeight: 1.3,
      textAlign: align,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      // maxWidth: '100%',
      // padding: '0 10px',
    };

    if (align === 'left') {
      base.transform = 'translateY(-50%)';
      base.textAlign = 'left';
      base.left = indent;
      base.right = 'auto';
      base.maxWidth = '66%';
    } else if (align === 'right') {
      // shift further to the right and allow earlier wrapping
      base.transform = 'translateY(-50%)';
      base.textAlign = 'right';
      base.left = 'auto';
      base.right = indent;
      base.maxWidth = '66%';
    } else {
      // center: use ~75% of available width and leave side gutters
      base.left = '50%';
      base.transform = 'translate(-50%,-50%)';
      base.textAlign = 'center';
      // base.maxWidth = '100%';
      // base.padding = '0 5px';
    }

    return base;
  }

  // Reduce font-size until the title fits within available vertical and horizontal space
  adjustTitleFontSize() {
    this.$nextTick(() => {
      const el = (this.$refs.heroTitle as unknown) as HTMLElement;
      if (!el) return;

      // find container height (v-img wrapper)
      const container = this.$el.querySelector('.img-pos') as HTMLElement || el.parentElement;
      const containerHeight = container ? container.clientHeight : el.clientHeight;
      if (!containerHeight || containerHeight <= 0) return;

      // allow the title to occupy up to ~60% of image height
      const maxHeight = Math.max(24, Math.floor(containerHeight * 0.6));

      // compute available horizontal space for the title based on container and alignment
      const align = (this.textAlign || 'center').toString().trim().toLowerCase();
      const containerWidth = container ? container.clientWidth : el.clientWidth;
      // pick an effective fraction depending on alignment (matches values in titleStyle)
      let allowedWidthPx = containerWidth;
      if (align === 'left') {
        allowedWidthPx = Math.floor(containerWidth * 0.7);
      } else if (align === 'right') {
        allowedWidthPx = Math.floor(containerWidth * 0.7);
      } else {
        if (containerWidth < 500) {
        allowedWidthPx = Math.floor(containerWidth * 0.65);
        } else {
          allowedWidthPx = Math.floor(containerWidth * 0.6);
        }
      }
      // subtract some gutter/padding to avoid exact-fit wrapping (smaller to trigger earlier)
      // allowedWidthPx = Math.max(32, allowedWidthPx - 20);
      // enforce maxWidth on the element so its inline layout is constrained
      // console.log(`Adjusting title font size. Container: ${containerWidth}px wide, ${containerHeight}px high. Allowed width for title: ${allowedWidthPx}px. Max height: ${maxHeight}px.`);
      // Set explicit width instead of maxWidth to ensure centered element actually occupies the calculated space
      el.style.width = allowedWidthPx + 'px';

      // start from a reasonable maximum and compute a dynamic minimum so text doesn't become unreadably small
      let maxFont = 32;
      let minFont = 12;
      // increase minimum for wider containers to keep readability (e.g. 450px -> ~20px)
      try {
        const responsiveMin = Math.floor(containerWidth / 22);
        if (!isNaN(responsiveMin) && responsiveMin > minFont) minFont = responsiveMin;
      } catch (e) { }

      // if computedFontSize already set, use it as starting point
      if (this.computedFontSize) {
        const cur = parseFloat(this.computedFontSize.replace('px', ''));
        if (!isNaN(cur)) maxFont = Math.max(cur, maxFont);
      }

      // Special rule: for narrow containers between 200 and 500px, cap maxFont lower
      // to avoid trying to squeeze long titles too large horizontally.
      try {
        if (containerWidth >= 200 && containerWidth <= 500) {
          const minCap = 20; // at 300px
          const maxCap = 32; // at 500px
          const t = (containerWidth - 300) / (500 - 300);
          const cap = Math.round(minCap + t * (maxCap - minCap));
          maxFont = Math.min(maxFont, cap);
        } else if (containerWidth < 200) {
          maxFont = minFont;
        }
      } catch (e) { }

      let font = maxFont;
      el.style.whiteSpace = 'normal';
      // prevent breaking words in the middle: prefer normal word-break and break-word wrapping
      el.style.wordBreak = 'normal';
      el.style.overflowWrap = 'break-word';

      // binary search to speed up adjustments — ensure both vertical and horizontal fit
      let lo = minFont;
      let hi = maxFont;
      let best = minFont;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = mid + 'px';
        // force reflow
        const sh = el.scrollHeight;
        // accept size only if it fits vertically
        const fitMargin = 0.98;
        if (sh <= Math.floor(maxHeight * fitMargin)) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      // apply an additional shrink so the text has some margin to fit
      // best = Math.max(minFont, Math.floor(best * 0.9));
      // ensure we never go below minFont
      best = Math.max(best, minFont);
      this.computedFontSize = best + 'px';
      el.style.fontSize = this.computedFontSize;
    });
  }

  // Adjust hex color brightness. percent > 0 lightens, < 0 darkens.
  adjustHexBrightness(hex: string, percent: number) {
    if (!hex || typeof hex !== 'string') return hex;
    let h = hex.trim();
    if (h[0] === '#') h = h.slice(1);
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return hex;
    const num = parseInt(h, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;

    const apply = (c: number) => {
      if (percent >= 0) {
        return Math.round(c + (255 - c) * (percent / 100));
      }
      return Math.round(c * (1 + percent / 100));
    };

    r = Math.max(0, Math.min(255, apply(r)));
    g = Math.max(0, Math.min(255, apply(g)));
    b = Math.max(0, Math.min(255, apply(b)));

    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  get alt() {
    if (this.altText && this.altText.trim().length > 0) {
      return this.altText.trim();
    }
    if (!this.settings?.altText || this.settings?.altText.trim().length === 0) {
      return "";
    }
    return this.settings?.altText;
  }

//   get widthInPixel() {
//     if (!this.width) return null;
//     const w = (this.width as any);
//     if (typeof w === 'number') return w + 'px';
//     const s = String(w);
//     if (s.endsWith("px") || s.endsWith("%")) return s;
//     return s + "px";
//   }

  get heightValue() {
    if (!this.height) return this.isFillContainer ? "100%" : null;
    const h = (this.height as any);
    if (typeof h === 'number') return h;
    const s = String(h);
    if (s.endsWith("px")) return s.substring(0, s.length - 2);
    return s;
  }
}
